import { useCallback } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { ExperienceFeatures } from '@interfaces/event';
import { isEventFeatureEnabled } from '@helpers/events';
import { isAIEnabledForEventSettings } from './aiEnabled';

/**
 * Exposes the assistant's mount gate for the loaded experience.
 *
 * @returns Predicate reporting whether the assistant is enabled right now; its
 *   identity changes whenever `currentEvent.settings` does, so a live
 *   `EVENT_SETTINGS_UPDATED` broadcast re-renders every consumer.
 */
export const useIsAIEnabledForEvent = () => {
  const { currentEventId, currentEvent } = useMytaverseEvent();

  return useCallback(() => {
    return isAIEnabledForEventSettings(currentEventId, currentEvent?.settings);
  }, [currentEventId, currentEvent?.settings]);
};

/**
 * Exposes the "Ask Me Anything" (free-form Q&A) gate for the loaded experience.
 *
 * Enabled by default — an absent `features.ASK_ME_ANYTHING` entry counts as
 * enabled, only an explicit `false` disables it.
 *
 * @returns Predicate reporting whether free-form Q&A is enabled right now.
 */
export const useIsAskMeAnythingEnabledForEvent = () => {
  const { currentEvent } = useMytaverseEvent() || {};

  return useCallback(() => {
    return isEventFeatureEnabled(
      currentEvent,
      ExperienceFeatures.AskMeAnything,
    );
  }, [currentEvent]);
};
